import axios from 'axios'
import { computed, useContext } from '@nuxtjs/composition-api'

// Composables
import isArray from 'lodash/fp/isArray'
import useAppSearch from '@/composables/search/useAppSearch'

// Store
import { useKnowledgeBaseStore } from '@/store/knowledge-base'

// Types
import { BreadcrumbsData } from '~/types/breadcrumbs'
import { SupportArticleInterface } from '@/types/support/knowledgeBaseTypes'

// Common
import { KNOWLEDGE_BASE_STOP_WORDS } from '~/common/static/learn-and-support'
import { firstCharUpperCase } from '@/common/utils/characters'

export default () => {
  // Constants
  const context = useContext()
  const knowledgeBaseStore = useKnowledgeBaseStore()
  const { getAxiosConfig, getElasticSearchEndpoint } = useAppSearch()
  const LEARN_AND_SUPPORT_URL_BASE = '/learn-and-support'
  const TECHNICAL_SUPPORT_URL_BASE = `${LEARN_AND_SUPPORT_URL_BASE}/technical-support`
  const techSupportErrorMessage =
    'Please try again or reach out to our <a target="blank" href="/learn-and-support/order-support#tech-support">Support</a> team for assistance.'
  const errorMessage = `There was an error submitting your feedback. ${techSupportErrorMessage}`
  const succesMessage = 'Thank you for your feedback.'
  const resultFields = {
    lastmodifieddate: {
      raw: {},
    },
    body: {
      raw: {},
      snippet: {},
    },
    title: {
      raw: {},
      snippet: {},
    },
    articleversionid: {
      raw: {},
    },
    createddate: {
      raw: {},
    },
    articlelink: {
      raw: {},
    },
    articlenumber: {
      raw: {},
    },
    urlname: {
      raw: {},
    },
  }

  // Format search input
  const formattedSearchInput = (input: string) => {
    const punctuation = /[.,?!]/g

    return input
      .replace(punctuation, '')
      .split(' ')
      .filter((word) => !KNOWLEDGE_BASE_STOP_WORDS.includes(word.toLowerCase()))
      .join(' ')
      .trim()
  }

  // get just one article
  const getSupportArticle = async (articlenumber: string) => {
    // TODO, check if the article is already in the store
    // if so, just return the article from the store
    // otherwise, fetch the article from the API, populate the store and return the article
    const articleInStore = knowledgeBaseStore.getResultFromArticleNumber(articlenumber)
    if (articleInStore) {
      knowledgeBaseStore.setLoading(false)
      return articleInStore
    }

    // Clear all existing errors
    knowledgeBaseStore.clearErrors()

    // Search in one field with the highest precision to match just one article
    const queryoptions = {
      query: articlenumber,
      search_fields: { articlenumber: {} },
      result_fields: resultFields,
      precision: 11,
      page: {
        size: 1,
      },
    }
    const axiosConfig = getAxiosConfig(queryoptions, getElasticSearchEndpoint('knowledge-base'))

    // Try to get the results or send the error to the log service
    try {
      const { data } = await axios(axiosConfig)

      // if we have one result, set the results in the store
      // if not, we can't find the article and we redirect back to the support page
      if (data.results.length === 1) {
        knowledgeBaseStore.setResults(data)
        return knowledgeBaseStore.results?.results![0] || null
      } else {
        context.redirect(getSupportArticlesLink())
        return {}
      }
    } catch (error: any) {
      // Add custom error for this request
      onSupportAxiosError(
        setCustomError(error, `There was an error when fetching the article. ${techSupportErrorMessage}`)
      )
    }
  }

  // search in the knowledge base
  const getSearchResults = async () => {
    const responseTimeStart = new Date()

    // Clear all existing errors
    knowledgeBaseStore.clearErrors()
    knowledgeBaseStore.setLoading(true)

    const queryoptions = {
      query: formattedSearchInput(knowledgeBaseStore.searchInput),
      result_fields: resultFields,
      page: {
        size: knowledgeBaseStore.page.resultsPerPage,
        current: knowledgeBaseStore.page.currentPage,
      },
    }

    const axiosConfig = getAxiosConfig(queryoptions, getElasticSearchEndpoint('knowledge-base'))

    try {
      // Fetch data and set the results in the store
      const { data } = await axios(axiosConfig)
      const responseTimeEnd = new Date()
      const responseTime = (responseTimeEnd.getTime() - responseTimeStart.getTime()) / 1000

      knowledgeBaseStore.setResults(data)
      knowledgeBaseStore.setResponseTime(responseTime)
    } catch (error: any) {
      // Add custom error for this request
      onSupportAxiosError(
        setCustomError(error, `There was an error when fetching the articles. ${techSupportErrorMessage}`)
      )
    } finally {
      knowledgeBaseStore.setLoading(false)
    }
  }

  // get typeahead results
  const getTypeaheadResults = async () => {
    // Clear all existing errors
    knowledgeBaseStore.clearErrors()

    const queryoptions = {
      query: formattedSearchInput(knowledgeBaseStore.searchInput),
      search_fields: { titlenohtml: {}, bodynohtml: {}, associatedproducts: {} },
      result_fields: {
        title: {
          raw: {},
          snippet: {},
        },
        urlname: {
          raw: {},
        },
        articlenumber: {
          raw: {},
        },
      },
      page: {
        size: 6,
      },
    }
    const axiosConfig = getAxiosConfig(queryoptions, getElasticSearchEndpoint('knowledge-base'))
    // Try to get the results or send the error to the log service
    try {
      const { data } = await axios(axiosConfig)
      knowledgeBaseStore.setTypeaheadResults(data)
    } catch (error: any) {
      // Clear previous results
      knowledgeBaseStore.clearResults('typeahead')

      onSupportAxiosError(
        setCustomError(error, `There was an error when fetching the typeahead results. ${techSupportErrorMessage}`)
      )
    }
  }

  // Create a record in the article feedback object in salesforce
  // Auth is done in our SF middleware
  const createKnowledgeBaseFeedbackRecord = async (options: {
    supportArticle: SupportArticleInterface
    selectedVote: string
  }) => {
    // Create the new record
    const newRecord = {
      Like__c: options.selectedVote === 'up' ? 1 : 0,
      Knowledge_Article_Id__c: options.supportArticle._meta.id,
      Knowledge_Article_Version_Id__c: options.supportArticle.articleversionid.raw,
    }

    // Send the feedback to the salesforce
    // Print error message in UI if there is an error and send to logService
    await axios
      .post('/api/salesforce/create-record', {
        newRecord,
        object: 'Article_Feedback__c',
      })
      .then((response) => {
        // Set the vote record id in the store
        if (response.data.id) {
          // Set loading
          knowledgeBaseStore.setLoading(false)

          // Set new record ID
          knowledgeBaseStore.setVoteRecordId(response.data.id)

          // Set the form feedback
          knowledgeBaseStore.setFormFeedback({
            showFeedback: false,
            showDescription: true,
          })
        }

        // Send error to LogService & form
        if (!response.data.success) {
          knowledgeBaseStore.setFormFeedback({ showFeedback: true, showDescription: false, errorMessage })
          context.$logService.sendError({ ...response.data, type: 'SalesforceAxiosError' })
        }
      })
      .catch((error) => {
        // Send error to LogService & form
        knowledgeBaseStore.setFormFeedback({ showFeedback: true, showDescription: false, errorMessage })
        context.$logService.sendError({ ...error, type: 'SalesforceAxiosError' })
      })
  }

  // Update an existing KB record
  const updateKnowledgeBaseFeedbackRecord = async (options: { feedback: String }) => {
    // Set loader
    knowledgeBaseStore.setLoading(true)
    const newData = {
      Feedback__c: options.feedback,
    }

    // Send the feedback to the salesforce
    // Print error message in UI if there is an error and send to logService
    await axios
      .post('/api/salesforce/update-record', {
        newData,
        recordId: knowledgeBaseStore.voteRecordId,
        object: 'Article_Feedback__c',
      })
      .then((response) => {
        // Change loader
        knowledgeBaseStore.setLoading(false)

        // Send error to LogService
        if (response.status !== 200) {
          knowledgeBaseStore.setFormFeedback({ showFeedback: true, showDescription: true, errorMessage })
          context.$logService.sendError({ ...response.data, type: 'SalesforceAxiosError' })
        } else {
          knowledgeBaseStore.setFormFeedback({ showFeedback: true, showDescription: false, succesMessage })
        }
      })
      .catch((error) => {
        // FE ui notifiction when we have an error response
        knowledgeBaseStore.setFormFeedback({ showFeedback: true, showDescription: false, errorMessage })
        // Send error to LogService
        context.$logService.sendError({ ...error, type: 'SalesforceAxiosError' })
      })
  }

  // Add a custom error message
  const setCustomError = (error: any, message: string) => {
    if (!error?.response?.data) {
      return error
    }
    if (!isArray(error?.response?.data?.errors)) {
      error.response.data = {
        errors: [],
      }
    }
    error.response.data.errors.push(message)

    return error
  }

  // Method to send the error to the log service
  const onSupportAxiosError = (error: any) => {
    // In case of a failure, log the error
    const logName = { type: 'ElasticSearchAxiosError' }
    const newError = { ...error, ...logName }
    context.$logService.sendError(newError)

    // Show the error message in the UI
    if (error.response) {
      const errorArray = Array.isArray(error.response.data)
        ? error.response.data[0].errors
        : error.response.data?.errors
      if (errorArray !== undefined) {
        errorArray.forEach((error: string) => setKnowledgebaseErrors(error))
      }
    } else if (error.message) {
      setKnowledgebaseErrors(error.message)
    } else if (error.request) {
      setKnowledgebaseErrors(error.request)
    }
  }

  /*
    Sets knowledgebase errors in the store

    RETURNS: void
  */
  const setKnowledgebaseErrors = (errorMessage: string) => {
    // check if this error message already exist
    if (!knowledgeBaseStore.knowledgebaseErrors.map((error: any) => error.message).includes(errorMessage)) {
      knowledgeBaseStore.knowledgebaseErrors.push({
        message: errorMessage,
      })
    }
  }

  // computed var to show if we have typeahead results
  const hasTypeaheadResults = computed(() => {
    return Object.keys(knowledgeBaseStore.typeahead.results).length > 0
  })

  // Returns the link to the support article
  const getSupportArticleLink = (articleNumber: string, urlName: string) => {
    return `${TECHNICAL_SUPPORT_URL_BASE}/${urlName.toLowerCase()}/${articleNumber}`
  }

  // Returns the link to the support articles page
  const getSupportArticlesLink = () => {
    // optionaly add the search query
    return knowledgeBaseStore.searchInput.length > 0
      ? `${TECHNICAL_SUPPORT_URL_BASE}/articles?q=${knowledgeBaseStore.searchInput}`
      : `${TECHNICAL_SUPPORT_URL_BASE}/articles`
  }

  // Create breadcrumb name for the article pages
  const getBreadcrumbArticleName = (articlename: string) => {
    const maxLength = 40
    const transformedNamed = firstCharUpperCase(articlename.replace(/-/g, ' '))
    if (articlename.length <= maxLength) {
      return transformedNamed
    }
    return transformedNamed.substring(0, maxLength) + '...'
  }

  // Return basic breadcrumb object for the learn-and-support pages
  const startBreadcrumbRoutes: BreadcrumbsData = [
    {
      label: 'Learn & Support',
      url: LEARN_AND_SUPPORT_URL_BASE,
    },
    {
      label: 'Technical Support',
      url: TECHNICAL_SUPPORT_URL_BASE,
    },
    {
      label: 'Articles',
      url: getSupportArticlesLink(),
    },
  ]

  // Check if the store has results
  const hasSupportResults = computed(() => {
    return knowledgeBaseStore.results?.results && Object.keys(knowledgeBaseStore.results).length > 0
  })

  return {
    getSupportArticle,
    getSearchResults,
    getTypeaheadResults,
    hasTypeaheadResults,
    getSupportArticleLink,
    getSupportArticlesLink,
    getBreadcrumbArticleName,
    hasSupportResults,
    startBreadcrumbRoutes,
    createKnowledgeBaseFeedbackRecord,
    updateKnowledgeBaseFeedbackRecord,
  }
}
